/**
 * SchedulerDashboard.jsx
 * Main scheduler interface displaying a calendar with exercise notifications.
 * Allows users to create, edit, and delete scheduled exercises with optional
 * repeat patterns. Integrates with Firebase for push notifications and handles
 * both real and optimistic (temporary) event states.
 *
 * Key responsibilities:
 *  - render FullCalendar with dynamic events
 *  - manage dialog for creating/editing notifications
 *  - handle event interactions (click, date select)
 *  - track temporary events during network requests
 *  - initialize push notifications and FCM setup
 *  - display snackbar messages and banners
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Button,
  Snackbar,
  Typography,
  Container,
  CircularProgress,
  Box,
} from "@mui/material";
import { Toast } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import FullCalendar from "@fullcalendar/react";
import listPlugin from "@fullcalendar/list";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useTranslation } from "react-i18next";
import { atcb_action } from "add-to-calendar-button";
import { useQueryClient } from "@tanstack/react-query";

import { useScheduler } from "../hooks/useScheduler";
import { useFirebaseNotifications } from "../hooks/useFirebaseNotifications";

import NotificationDialog from "../components/scheduler/NotificationDialog";
import NotificationBanner from "../components/scheduler/NotificationBanner";

import { isSafari } from "@firebase/util";
import { useAuthStore, useTourStore } from "../stores/zustand";

/**
 * SchedulerDashboard component (memoized)
 * Main component for the scheduler dashboard. Memoization ensures it only
 * re-renders when the three login-related props change.
 */
const SchedulerDashboard = React.memo(
  ({ isFirstLogin = false, wasAutoLogin = false, wasStartup = false }) => {
    const { t } = useTranslation();
    const { startTour } = useTourStore();

    const queryClient = useQueryClient();
    const email = useAuthStore((state) => state.user.email);
    const currentLanguage = useAuthStore((state) => state.preferredLanguage);
    const currentProduct = useAuthStore((state) => state.currentProduct);
    const isPhysioUser = currentProduct === "physio_user";

    //  Refs
    const calendarRef = useRef(null);
    const calendarContainerRef = useRef(null);
    const lastClickedEventIdRef = useRef(null); // Track last clicked event

    // Local UI State
    // Dialog and form management
    const [calendarHeight, setCalendarHeight] = useState("auto");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingData, setEditingData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const getTimezoneOffset = () => {
      const offset = new Date().getTimezoneOffset() / -60;
      return offset >= 0
        ? `+${String(Math.abs(offset)).padStart(2, "0")}`
        : `-${String(Math.abs(offset)).padStart(2, "0")}`;
    };
    const timezoneOffset = getTimezoneOffset();

    // Initialize Firebase notifications hook
    const firebaseNotifications = useFirebaseNotifications();

    // Initialize scheduler hook (manages all CRUD operations and state)
    const scheduler = useScheduler();

    // Destructure scheduler state and actions
    const {
      exerciseTypes,
      userId,
      error: schedulerError,
      calendarEvents,
      schedules,
      createNotification,
      editNotification,
      editSchedule,
      deleteSchedule,
      deleteNotification,
      refreshData,
      schedulerIsFetching,
      homeIsLoading,
      getCalendarToken,
    } = scheduler;

    // Destructure Firebase notification utilities
    const {
      showNotificationBanner,
      handleEnablePushNotifications,
      handleOpenNotificationGuide,
      safariPushAlertToken,
      registerSafariToken,
      isPermissionDenied,
    } = firebaseNotifications;

    // Handle first-login setup (start guided tour)
    // Also log app launch context (auto-login, startup)
    useEffect(() => {
      if (isFirstLogin) {
        startTour(); // Start guided tour for new users
      }
    }, [isFirstLogin, startTour]);

    // Observe calendar container resize to adjust FullCalendar height dynamically
    useEffect(() => {
      const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0].target) {
          const rect = entries[0].target.getBoundingClientRect();
          setCalendarHeight(rect.height);
        }
      });

      const currentContainer = calendarContainerRef.current;
      if (currentContainer) {
        resizeObserver.observe(currentContainer);
      }

      return () => {
        if (currentContainer) {
          resizeObserver.unobserve(currentContainer);
        }
        resizeObserver.disconnect();
      };
    }, []);

    // Register Safari push token when it becomes available (iOS Safari)
    useEffect(() => {
      if (safariPushAlertToken && isSafari()) {
        registerSafariToken(safariPushAlertToken);
      }
    }, [safariPushAlertToken, registerSafariToken]);

    // Initialize FCM (Firebase Cloud Messaging) for Electron app
    // Prevents duplicate initialization and manages token listeners
    useEffect(() => {
      const isElectron =
        typeof window !== "undefined" && !!window.electronPushReceiver;
      const isAuthenticated = useAuthStore.getState().isAuthenticated();

      if (isElectron && isAuthenticated) {
        const fcmInitialized = sessionStorage.getItem("fcm_initialized");
        if (fcmInitialized) {
          return;
        }

        sessionStorage.setItem("fcm_initialized", "true");

        let fcmServiceStarted = false;
        let tokenListener, errorListener;

        tokenListener = window.electronPushReceiver?.onToken?.((token) => {
          fcmServiceStarted = true;
          if (tokenListener) tokenListener();
        });

        errorListener = window.electronPushReceiver?.onError?.((error) => {
          console.error("[SchedulerDashboard] FCM service error:", error);
        });

        const startTimeout = setTimeout(() => {
          if (
            !fcmServiceStarted &&
            window.electronAPI?.startNotificationService
          ) {
            window.electronAPI.startNotificationService();
          }
        }, 1000);

        const cleanupTimeout = setTimeout(() => {
          if (!fcmServiceStarted && tokenListener) {
            tokenListener();
          }
        }, 10000);

        return () => {
          if (tokenListener) tokenListener();
          if (errorListener) errorListener();
          clearTimeout(startTimeout);
          clearTimeout(cleanupTimeout);
          sessionStorage.removeItem("fcm_initialized");
        };
      }
    }, []);

    // Show temporary snackbar notification (auto-hides after 3 seconds)
    const displaySnackbarMessage = useCallback((message) => {
      setSnackbarMessage(message);
      setSnackbarOpen(true);
      setTimeout(() => setSnackbarMessage(""), 3000);
    }, []);

    // Open notification dialog for creating a new event
    const handleOpenDialog = useCallback(() => {
      setEditingData(null);
      setIsEditing(false);
      setIsDialogOpen(true);
    }, []);

    // Close dialog with delay to allow fade-out animation
    const handleCloseDialog = useCallback(() => {
      setIsDialogOpen(false);
      setTimeout(() => {
        setEditingData(null);
        setIsEditing(false);
      }, 300);
    }, []);

    // Submit notification form (create or update)
    // Routes to appropriate scheduler action based on edit type
    const handleFormSubmit = useCallback(
      async (formData) => {
        try {
          // Handle schedule edit (entire series)
          if (isEditing && editingData?.isScheduleEdit) {
            editSchedule({
              scheduleId: editingData.scheduleId,
              notificationData: formData,
            });
          }
          // Handle single notification edit
          else if (isEditing) {
            editNotification({
              notificationId: editingData.notificationId,
              notificationData: formData,
            });
          }
          // Create new notification
          else {
            createNotification(formData);
          }
          displaySnackbarMessage(
            isEditing ? t("notificationUpdated") : t("notificationCreated"),
          );
          handleCloseDialog();
        } catch (error) {
          console.error("Error message:", error.message);
          displaySnackbarMessage(t("unexpectedError"));
        }
      },
      [
        isEditing,
        editingData,
        editSchedule,
        editNotification,
        createNotification,
        handleCloseDialog,
        displaySnackbarMessage,
        t,
      ],
    );

    // Handle calendar event click: open event in dialog for editing
    // Differentiates between temporary (optimistic) and real events
    const handleEventClick = useCallback(
      (info) => {
        const eventId = info.event.id;
        lastClickedEventIdRef.current = eventId;

        // Check if this is a temporary event (optimistic update)
        const isTemp = String(eventId).startsWith("temp-");

        if (isTemp) {
          //  Load from cache instead of optimisticEvents Map
          const currentHomeData = queryClient.getQueryData([
            "homeData",
            email,
            timezoneOffset,
            currentLanguage,
          ]);

          const schedule = currentHomeData?.schedules.find(
            (s) => s.id === eventId,
          );

          if (schedule) {
            // Convert notificationTime to local time
            const localDate = new Date(schedule.notificationTime);
            const year = localDate.getFullYear();
            const month = String(localDate.getMonth() + 1).padStart(2, "0");
            const day = String(localDate.getDate()).padStart(2, "0");
            const date = `${year}-${month}-${day}`;
            const time = localDate.toTimeString().slice(0, 5);

            const dialogData = {
              date: date || "",
              time: time || "",
              exerciseTypes: [schedule.preferenceId],
              repeatInterval: schedule.repeatInterval || "NONE",
              repeatPeriod: schedule.repeatPeriod?.split("T")[0] || "",
              scheduleId: schedule.id,
              notificationId: schedule.notificationId,
              type: schedule.type,
              video: schedule.video,
              name: schedule.name,
              isScheduleEdit: true,
            };

            setEditingData(dialogData);
            setIsEditing(true);
            setIsDialogOpen(true);
            return;
          } else {
            console.warn(
              "[handleEventClick] Temp event not found in cache:",
              eventId,
            );
            return;
          }
        }

        // Load real event from fetched schedules
        const schedule = schedules.find(
          (s) => Number(s.id) === Number(eventId),
        );
        if (!schedule) {
          console.warn("[handleEventClick] Schedule not found:", eventId);
          return;
        }

        // Convert notificationTime to local time
        const localDate = new Date(schedule.notificationTime);
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, "0");
        const day = String(localDate.getDate()).padStart(2, "0");
        const date = `${year}-${month}-${day}`;
        const time = localDate.toTimeString().slice(0, 5);

        const dialogData = {
          date: date || "",
          time: time || "",
          exerciseTypes: [schedule.preferenceId],
          repeatInterval: schedule.repeatInterval || "NONE",
          repeatPeriod: schedule.repeatPeriod?.split("T")[0] || "",
          scheduleId: schedule.id,
          notificationId: schedule.notificationId,
          type: schedule.type,
          video: schedule.video,
          name: schedule.name,
          isScheduleEdit: true,
        };

        setEditingData(dialogData);
        setIsEditing(true);
        setIsDialogOpen(true);
      },
      [schedules, queryClient, email, timezoneOffset, currentLanguage],
    );

    // Handle calendar date click: open dialog to create event on selected date
    // Pre-fill date, time, and default exercise type
    const handleDateClick = useCallback(
      (info) => {
        const selectedEventDate = info.dateStr;
        const currentDate = new Date();
        const hours = currentDate.getHours();
        const minutes = currentDate.getMinutes();
        const remainder = minutes % 5;
        let roundedMinutes =
          remainder === 0 ? minutes : minutes + (5 - remainder);
        let roundedHours = hours;
        if (roundedMinutes >= 60) {
          roundedMinutes = 0;
          roundedHours = (roundedHours + 1) % 24;
        }
        const currentTime = `${String(roundedHours).padStart(2, "0")}:${String(roundedMinutes).padStart(2, "0")}`;

        const defaultData = {
          date: selectedEventDate,
          time: currentTime,
          exerciseTypes: exerciseTypes.length > 0 ? [exerciseTypes[0].id] : [],
          repeatInterval: "NONE",
          repeatPeriod: "",
        };

        setEditingData(defaultData);
        setIsEditing(false);
        setIsDialogOpen(true);
      },
      [exerciseTypes],
    );

    // Delete a schedule or notification series
    // Routes to appropriate delete handler based on type
    const handleDeleteSchedule = useCallback(
      async (scheduleId, deleteType) => {
        try {
          // Delete entire series or single instance
          let result = false;
          if (deleteType === "series") {
            result = await deleteNotification(scheduleId);
          } else {
            result = await deleteSchedule(scheduleId);
          }

          displaySnackbarMessage(
            result ? t("notificationDeleted") : t("failedToDeleteNotification"),
          );

          if (result) {
            handleCloseDialog();
          }
        } catch (error) {
          console.error("Delete error:", error);
          displaySnackbarMessage(t("unexpectedError"));
        }
      },
      [
        deleteSchedule,
        deleteNotification,
        handleCloseDialog,
        displaySnackbarMessage,
        t,
      ],
    );

    // Open add-to-calendar dialog to export events to Google, Outlook, Apple, etc.
    // Uses add-to-calendar-button library for multi-service support
    const handleAddToCalendar = useCallback(async () => {
      if (!userId) {
        displaySnackbarMessage(t("userIdNotAvailable"));
        return;
      }

      const token = await getCalendarToken();
      // Configure iCalendar export options
      const config = {
        name: "Dynamic Desk Calendar",
        startDate: "2023-10-01",
        icsFile: `${process.env.REACT_APP_FIREBASE_API_HOST}/api/ical/${token}`,
        iCalFileName: "dynamic_desk_calendar",
        subscribe: true,
        options: ["Apple", "Google", "Outlook.com", "iCal"],
        timeZone: "Europe/Berlin",
        trigger: "click",
      };

      try {
        atcb_action(config);
      } catch (error) {
        console.error("Failed to add to calendar:", error);
        displaySnackbarMessage(t("failedToAddToCalendar"));
      }
    }, [userId, displaySnackbarMessage, t, getCalendarToken]);

    // Close snackbar notification
    const handleSnackbarClose = useCallback(() => {
      setSnackbarOpen(false);
    }, []);

    // Watch temporary event becoming real while dialog is open
    // When a temp event (optimistic update) is confirmed by server,
    // update dialog data to reflect the real event ID
    useEffect(() => {
      if (isDialogOpen && editingData && isEditing) {
        const currentScheduleId = editingData.scheduleId;
        const isTemp = String(currentScheduleId).startsWith("temp-");

        if (isTemp) {
          // Check if this temp event has become real by looking in cache
          const currentHomeData = queryClient.getQueryData([
            "homeData",
            email,
            timezoneOffset,
            currentLanguage,
          ]);

          const tempSchedule = currentHomeData?.schedules.find(
            (s) => s.id === currentScheduleId,
          );

          if (!tempSchedule) {
            // Temp event no longer in cache - it became real
            // Find the real event by matching time and preferenceId
            const tempDate = editingData.date;
            const tempTime = editingData.time;
            const tempPreferenceId = editingData.exerciseTypes?.[0];

            const matchingSchedule = schedules.find((s) => {
              const localDate = new Date(s.notificationTime);
              const year = localDate.getFullYear();
              const month = String(localDate.getMonth() + 1).padStart(2, "0");
              const day = String(localDate.getDate()).padStart(2, "0");
              const sDate = `${year}-${month}-${day}`;
              const sTime = localDate.toTimeString().slice(0, 5);

              return (
                sDate === tempDate &&
                sTime === tempTime &&
                (s.preferenceId === tempPreferenceId || s.preferenceId === null)
              );
            });

            if (matchingSchedule) {
              // Update editingData with real schedule
              const localDate = new Date(matchingSchedule.notificationTime);
              const year = localDate.getFullYear();
              const month = String(localDate.getMonth() + 1).padStart(2, "0");
              const day = String(localDate.getDate()).padStart(2, "0");
              const date = `${year}-${month}-${day}`;
              const time = localDate.toTimeString().slice(0, 5);

              const updatedDialogData = {
                date: date || "",
                time: time || "",
                exerciseTypes: [matchingSchedule.preferenceId],
                repeatInterval: matchingSchedule.repeatInterval || "NONE",
                repeatPeriod:
                  matchingSchedule.repeatPeriod?.split("T")[0] || "",
                scheduleId: matchingSchedule.id,
                notificationId: matchingSchedule.notificationId,
                type: matchingSchedule.type,
                video: matchingSchedule.video,
                name: matchingSchedule.name,
                isScheduleEdit: true,
              };

              setEditingData(updatedDialogData);
            }
          }
        }
      }
    }, [
      isDialogOpen,
      editingData,
      isEditing,
      schedules,
      queryClient,
      email,
      timezoneOffset,
      currentLanguage,
    ]);

    // Memoized calendar component to prevent unnecessary re-renders
    // Dependencies: calendar events, height, language, translation, and event handlers
    // Only recalculates when calendar data or language changes
    const calendarComponent = useMemo(
      () => (
        <div style={{ width: "100%", flex: 1 }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={calendarEvents}
            contentHeight={calendarHeight - 200}
            locale={currentLanguage}
            buttonText={{ today: t("today") }}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            firstDay={1}
            dayMaxEvents={3}
            eventTimeFormat={{
              hour: "numeric",
              minute: "2-digit",
              meridiem: "short",
            }}
          />
        </div>
      ),
      [
        calendarEvents,
        calendarHeight,
        currentLanguage,
        t,
        handleEventClick,
        handleDateClick,
      ],
    );

    // Error state: display centered error message with retry option
    if (schedulerError) {
      return (
        <Container
          maxWidth="lg"
          sx={{
            height: "90vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box textAlign="center">
            <Typography variant="h6" color="error" gutterBottom>
              {t("failedToLoadData")}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {schedulerError?.message || String(schedulerError)}
            </Typography>
            <Button variant="contained" onClick={refreshData} sx={{ mt: 2 }}>
              {t("retry")}
            </Button>
          </Box>
        </Container>
      );
    }

    // Main calendar UI: renders FullCalendar with control buttons and legend
    // Displays loading spinner on initial load; shows sync indicator during data updates
    return (
      <Container maxWidth="lg" sx={{ height: "90vh" }}>
        <div
          ref={calendarContainerRef}
          style={{
            marginTop: "0.75rem",
            width: "100%",
            height: "80%",
            justifyContent: "center",
            alignContent: "center",
            position: "relative",
          }}
          className="white-background"
        >
          {/* FULL LOADING: Only on first load */}
          {homeIsLoading && calendarEvents.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {calendarComponent}

              {/* SUBTLE SYNC DOT */}
              {schedulerIsFetching && (
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 10,
                  }}
                >
                  <CircularProgress size={20} thickness={5} />
                </div>
              )}

              {/* BUTTONS & LEGEND */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "1rem",
                  marginBottom: "0.5rem",
                }}
              >
                <Button
                  className="add-entry-button"
                  variant="contained"
                  onClick={handleOpenDialog}
                  disabled={homeIsLoading}
                >
                  {t("addEntry")}
                </Button>

                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "1rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginRight: "0.5rem",
                      }}
                    >
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          backgroundColor: "green",
                          borderRadius: "50%",
                          marginRight: "0.25rem",
                        }}
                      ></div>
                      <span>{t("physio")}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginRight: "0.5rem",
                      }}
                    >
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          backgroundColor: "black",
                          borderRadius: "50%",
                          marginRight: "0.25rem",
                        }}
                      ></div>
                      <span>{t("settings")}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginRight: "0.5rem",
                      }}
                    >
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          backgroundColor: "blue",
                          borderRadius: "50%",
                          marginRight: "0.25rem",
                        }}
                      ></div>
                      <span>{t("user")}</span>
                    </div>
                  </div>
                </div>

                <div className="addToCalendarButton">
                  <Button
                    className="add-to-calendar-button"
                    variant="contained"
                    onClick={handleAddToCalendar}
                    disabled={!userId}
                  >
                    {t("addToCalendar")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* DIALOGS & BANNERS */}
        <NotificationDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onSubmit={handleFormSubmit}
          onDelete={handleDeleteSchedule}
          exerciseTypes={exerciseTypes}
          initialData={editingData}
          isEditing={isEditing}
          loading={homeIsLoading}
          isPhysioUser={isPhysioUser}
        />

        <NotificationBanner
          show={showNotificationBanner}
          onEnableNotifications={handleEnablePushNotifications}
          onShowGuide={handleOpenNotificationGuide}
          isDenied={isPermissionDenied}
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
        >
          <Toast.Body>{snackbarMessage}</Toast.Body>
        </Snackbar>
      </Container>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isFirstLogin === nextProps.isFirstLogin &&
      prevProps.wasAutoLogin === nextProps.wasAutoLogin &&
      prevProps.wasStartup === nextProps.wasStartup
    );
  },
);

export default SchedulerDashboard;
